import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bot, Database, Shield, BarChart3, FileText, Cpu, Zap, Layers,
  BookOpen, Globe, ArrowRight, Users, Beaker, CheckCircle, Workflow
} from 'lucide-react';

const fade = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const FEATURES = [
  { icon: Bot, title: 'Conversational AI Interface', color: 'text-primary',
    desc: 'Interact with specialized AI agents through natural language. Type "Run NCA" or "Build a PopPK model" and watch the analysis execute with real-time streaming results.',
    details: ['13 specialized agents with domain expertise', 'Intent classification routes to the right specialist', 'Suggested actions guide your next steps', 'Agent badges show which specialist is responding'] },
  { icon: Database, title: 'Automated Data Profiling', color: 'text-emerald-400',
    desc: 'Upload CSV/XPT/SAS datasets and get instant profiling. Column roles auto-detected, quality flags generated, interactive spaghetti plots rendered.',
    details: ['Auto-detects ID, TIME, DV, AMT, EVID columns', 'PK column role detection via naming patterns', 'Quality flags: BLQ%, sparse subjects, missing data', 'Interactive Plotly spaghetti plots with dose coloring'] },
  { icon: Beaker, title: 'Non-Compartmental Analysis', color: 'text-blue-400',
    desc: 'Complete NCA with 11 PK parameters per subject using the industry-standard linear-up/log-down trapezoidal rule.',
    details: ['Cmax, Tmax, AUC, t½, CL/F, Vz/F, MRT, Vss', 'Lambda-z with R² quality assessment', 'Bioequivalence (90% CI, TOST, 80-125%)', 'Dose proportionality (power model with CI)'] },
  { icon: Cpu, title: 'Population PK Modeling', color: 'text-purple-400',
    desc: 'Fit 1-CMT and 2-CMT models with IIV estimation. SHAP-based covariate screening. NONMEM and nlmixr2 code generation.',
    details: ['Automated 1-CMT vs 2-CMT comparison', 'Individual parameter estimation via scipy', 'SHAP + univariate covariate screening', 'NONMEM control stream generation'] },
  { icon: Shield, title: 'Privacy-First Architecture', color: 'text-cyan-400',
    desc: 'Raw patient data never reaches the AI model. SchemaExtractor creates metadata-only summaries before every LLM interaction.',
    details: ['Column names, types, and aggregate stats only', 'No individual rows transmitted to LLM', 'Local computation via scipy/numpy/pandas', 'Cryptographic audit trail for traceability'] },
  { icon: BarChart3, title: 'Interactive Visualizations', color: 'text-amber-400',
    desc: '15+ chart types rendered with Plotly.js. Spaghetti plots, VPC, GOF gallery, forest plots, E-R curves, target attainment heatmaps.',
    details: ['Interactive hover, zoom, pan on all charts', 'Log/linear toggle on concentration plots', 'Copy, download, and expand buttons on every widget', 'Publication-quality PNG export'] },
  { icon: Layers, title: '76 Workflow Templates', color: 'text-orange-400',
    desc: 'Pre-built analysis pipelines across 11 categories. Each template has ordered steps, agent assignments, parameters, and human review gates.',
    details: ['NCA, PopPK, PKPD, E-R, PBPK, Simulation', 'Special populations, DDI, Study design', 'Configurable parameters with defaults', 'Human review gates at decision points'] },
  { icon: FileText, title: 'Regulatory Report Generation', color: 'text-emerald-400',
    desc: 'Generate FDA/EMA-formatted DOCX documents with NCA summary tables, methods sections, and labeling language.',
    details: ['3 report templates (FDA PopPK, NCA, E-R)', 'Times New Roman formatting for submission', 'Auto-generated methods from audit trail', 'Section 12.3 labeling language draft'] },
  { icon: Globe, title: 'Regulatory Intelligence', color: 'text-sky-400',
    desc: 'RAG-powered search over 24 FDA/EMA/ICH/PMDA guidances. Cited responses with section references. Requirements checklists.',
    details: ['Vector search via Qdrant', '24 indexed regulatory guidances', 'FDA vs EMA comparison capability', 'Analysis-specific requirements checklists'] },
  { icon: CheckCircle, title: '15-Point Quality Control', color: 'text-red-400',
    desc: 'Comprehensive diagnostic suite: GOF gallery, VPC, bootstrap, shrinkage assessment, parameter plausibility, convergence checks.',
    details: ['6-panel GOF gallery (DV vs PRED, CWRES, QQ)', 'Visual Predictive Check with prediction intervals', 'Bootstrap with 95% CI and bias assessment', 'Traffic-light verdict: PASS / CONDITIONAL / FAIL'] },
  { icon: Zap, title: 'Monte Carlo Simulation', color: 'text-violet-400',
    desc: 'Generate virtual populations, calculate target attainment, optimize dosing regimens. Prediction interval ribbons and heatmaps.',
    details: ['Lognormal PK parameter simulation', 'Target attainment probability by dose', 'Prediction interval ribbon plots', 'DDI prediction with 8 perpetrator drugs'] },
  { icon: Workflow, title: 'Audit Trail', color: 'text-rose-400',
    desc: 'Every tool invocation recorded with SHA-256 hashed inputs/outputs in a cryptographic chain. Verifiable computational traceability.',
    details: ['Hash-chained immutable entries', 'Tamper detection via chain verification', 'Agent, tool, timestamp per entry', 'Regulatory-ready documentation'] },
];

export default function FeaturesPage() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 sm:pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[-0.02em]" style={{ fontFamily: 'Space Grotesk' }}>
              Platform Capabilities
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
              Every capability designed for clinical pharmacologists. 13 AI agents, 151 tools, 76 templates — covering the complete pharmacometric workflow from data ingestion to regulatory submission.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="pb-16 px-4 sm:px-6">
        <div className="max-w-[1100px] mx-auto space-y-6">
          {FEATURES.map((f, i) => (
            <motion.div key={i} {...fade} transition={{ delay: Math.min(i * 0.04, 0.3) }}>
              <Card className="card-hover overflow-hidden">
                <CardContent className="py-6 px-5 sm:px-8">
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-xl bg-muted/30 flex items-center justify-center`}>
                        <f.icon className={`w-6 h-6 ${f.color}`} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{f.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4">
                        {f.details.map((d, j) => (
                          <div key={j} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                            <span>{d}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[900px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { v: '13', l: 'AI Agents' }, { v: '151', l: 'Computational Tools' },
            { v: '76', l: 'Workflow Templates' }, { v: '24', l: 'Regulatory Guidances' },
          ].map(s => (
            <div key={s.l}>
              <p className="text-3xl font-bold font-mono text-primary">{s.v}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4 sm:px-6 border-t border-border/30">
        <div className="max-w-[500px] mx-auto text-center">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Ready to explore?</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
            <Button onClick={() => navigate('/architecture')} variant="outline" className="rounded-full h-10 px-5 text-sm">
              View Architecture
            </Button>
            <Button onClick={() => navigate('/signup')} className="rounded-full h-10 px-5 text-sm">
              Try App <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
